import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import breakpoint from 'styled-components-breakpoint';

import Grid from './Grid';
import Cell from './Cell';
import Block from './Block';
import H2 from './H2';
import H4 from './H4';
import P from './P';

const Line = styled.div`
  position: absolute;
  width: 2px;
  left: calc(50% - 1px);

  height: calc(100% - calc(100% / ${({ steps }) => steps}));
  top: calc(calc(100% / ${({ steps }) => steps}) / 2);

  z-index: -1;
  background-color: ${({ theme }) => theme.colors.grayDark};

  ${breakpoint('tablet')`
    height: 2px;
    top: 25px;

    width: calc(100% - calc(100% / ${({ steps }) => steps}));
    left: calc(calc(100% / ${({ steps }) => steps}) / 2);
  `};
`;

const StepIcon = styled.img`
  flex-basis: 45px;
  max-width: 125px;
  margin-bottom: ${({ theme, isVertical }) =>
    isVertical ? theme.spacing.baseUnit : ''};
  margin-right: ${({ isVertical }) => (isVertical ? '' : '2rem')};
  background-color: ${({ theme, isDark }) =>
    isDark ? theme.colors.transparent : theme.colors.white};
`;

const PageSteps = ({ content }) => {
  const isVertical = content.primary.direction === 'vertical';
  const isDark = content.primary.background_color === 'blueDarker';

  return (
    <Block
      id={content.primary.internal_link_id}
      pt={{ mobile: 'baseUp4', tablet: 'baseUp12' }}
      pb={{ mobile: isVertical ? '0' : 'baseUp4', tablet: 'baseUp12' }}
      backgroundColor={content.primary.background_color}
    >
      <H2
        textAlign="center"
        mb={{ mobile: 'baseUnit', tablet: 'baseUp10' }}
        color={isDark ? 'coralLight' : 'blueDark'}
      >
        {content.primary.title1.text}
      </H2>

      <Grid position="relative">
        {isVertical ? <Line steps={content.items.length} /> : null}

        {content.items.map((step, i) => (
          <Cell
            key={`step-${step.title1.text}-${i}`}
            size={{ mobile: 1, tablet: 1 / content.items.length }}
            my={{
              mobile: isVertical ? 'baseUp2' : '0',
              tablet: '0',
            }}
            backgroundColor={{
              mobile: isDark ? content.primary.background_color : 'white',
              tablet: 'transparent',
            }}
          >
            <Block
              display="flex"
              justifyContent="flex-start"
              flexDirection={isVertical ? 'column' : 'row'}
              alignItems="center"
              px={{
                mobile: isVertical ? 'baseUp10' : 'baseUnit',
                tablet: 'baseUp4',
              }}
              py={{ mobile: 'baseUnit', tablet: '0' }}
            >
              <StepIcon
                src={step.icon.url}
                alt={step.icon.alt}
                isDark={isDark}
                isVertical={isVertical}
              />
              <Block>
                <H4 textAlign={isVertical ? 'center' : 'left'} mb="baseDown2">
                  {step.title1.text}
                </H4>
                <P
                  textAlign={isVertical ? 'center' : 'left'}
                  color={isDark ? 'white' : ''}
                  mb="0"
                >
                  {step.text.text}
                </P>
              </Block>
            </Block>
          </Cell>
        ))}
      </Grid>
    </Block>
  );
};

PageSteps.propTypes = {
  content: PropTypes.shape({
    primary: PropTypes.shape({
      internal_link_id: PropTypes.string,
      title1: PropTypes.shape({ text: PropTypes.string }),
      background_color: PropTypes.string,
      direction: PropTypes.string,
    }),
    items: PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.shape({ url: PropTypes.string }),
        text: PropTypes.shape({ text: PropTypes.string }),
        title1: PropTypes.shape({ text: PropTypes.string }),
      }),
    ),
  }).isRequired,
};

export default PageSteps;

export const query = graphql`
  fragment PageStepsFragment on PrismicPageBodySteps {
    id
    slice_type
    primary {
      internal_link_id
      background_color
      direction
      title1 {
        text
      }
    }
    items {
      icon {
        alt
        url
      }
      title1 {
        text
      }
      text {
        text
      }
    }
  }
`;
